/**
 * IntegrationFormDialog Component
 * Dialog form for configuring integration connections
 */

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Form, FormItem } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useIntegrationManager } from '@/hooks/use-integration-manager'
import type { IntegrationFormData } from '@/services/integration-service'
import { Save, TestTube } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Integration } from '../agents/builder/integrations/types'
import { ApiKeyConnector, BasicAuthConnector, KeyPairConnector, OAuth2Connector } from './connectors'

// Integration form props types

interface IntegrationFormDialogProps {
  open: boolean
  integration: Integration | null
  onClose: () => void
  onSuccess?: (data: IntegrationFormData) => void
}

/**
 * Integration form dialog component
 */
export function IntegrationFormDialog({
  open,
  integration,
  onClose,
  onSuccess,
}: IntegrationFormDialogProps) {

  // Use the integration manager hook
  const { state: managerState, saveIntegration, testConnection, resetErrors } = useIntegrationManager()

  const form = useForm<IntegrationFormData>({
    defaultValues: {
      name: integration?.name || '',
      description: '',
      provider: integration?.connections[0]?.type || '',
      appId: integration?.id || '',
      connectionId: integration?.connections[0]?.id || '',
    },
  })

  /**
   * Reset form when integration changes
   */
  useEffect(() => {
    if (integration) {
      form.reset({
        name: integration.name,
        description: '',
        provider: integration.connections[0]?.type,
        appId: integration.id,
      })
      resetErrors()
    }
  }, [integration, form, resetErrors])

  /**
   * Handle form submission
   */
  const onSubmit = async (values: IntegrationFormData) => {
    if (!integration) return

    try {
      // Get form values from all connector components
      const formData = form.getValues()

      // Build integration payload
      const integrationData: IntegrationFormData = {
        description: formData.description || '',
        appId: integration.id,
        connectionId: integration.connections[0]?.id || '',
        // Include all form fields in the payload
        ...formData,
      }

      // Call the service to save integration
      const result = await saveIntegration(integrationData)

      if (result.success) {
        // Call success callback
        onSuccess?.({
          ...values,
          config: result.data as any,
        })

        // Close dialog
        handleClose()
      }
      // Error handling is done in the hook with toasts
    } catch (error) {
      console.error('Failed to save integration:', error)
    }
  }

  /**
   * Handle dialog close
   */
  const handleClose = () => {
    form.reset()
    resetErrors()
    onClose()
  }

  /**
   * Test connection (for database and API integrations)
   */
  const handleTestConnection = async () => {
    const formData = form.getValues()

    if (!integration || !formData.name) {
      return
    }

    try {
      // Build test payload
      const testPayload: IntegrationFormData = {
        appId: integration.id,
        connectionId: integration.connections[0]?.id || '',
        ...formData,
      }

      // Call the service to test connection
      await testConnection(testPayload)
      // Result handling is done in the hook with toasts
    } catch (error) {
      console.error('Connection test failed:', error)
    }
  }


  /**
   * Render provider-specific configuration fields
   */
  const renderProviderConfig = () => {
    if (!integration) return null

    switch (integration.connections[0]?.type) {
      case 'apiKey':
        return (
          <ApiKeyConnector
            form={form}
          />
        )

      case 'basic':
        return (
          <BasicAuthConnector
            form={form}
          />
        )

      case 'oauth2':
        return (
          <OAuth2Connector />
        )

      case 'keyPair':
        return (
          <KeyPairConnector
            form={form}
          />
        )

      default:
        return (
          <div className="text-sm text-muted-foreground">
            No additional configuration required for this integration type.
          </div>
        )
    }
  }

  if (!integration) return null

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="size-8 rounded-md">
              <AvatarImage src={integration.logoUrl} alt={integration.name} />
              <AvatarFallback>
                {integration.name.slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            Configure {integration.name}
          </DialogTitle>
          <DialogDescription>
            {integration.description}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Integration Name */}
            <FormItem>
              <Label htmlFor="name">Integration Name</Label>
              <Input
                id="name"
                placeholder="Enter a name for this integration"
                {...form.register('name', { required: true })}
              />
              <p className="text-xs text-muted-foreground">
                Name to identify this integration in your agent.
              </p>
            </FormItem>

            {/* Description */}
            <FormItem>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder="Brief description of this integration"
                rows={2}
                {...form.register('description')}
              />
            </FormItem>

            {/* Separator */}
            <div className="border-t border-dashed border-gray-200 my-4" />

            {/* Provider-specific configuration */}
            <div className="space-y-4">
              {renderProviderConfig()}
            </div>
          </form>
        </Form>

        <DialogFooter>
          <div className="flex items-center gap-2">
            {/* Test Connection Button */}
            {integration?.connections[0]?.type !== 'oauth2' && (
              <Button
                type="button"
                variant="outline"
                onClick={handleTestConnection}
                disabled={managerState.isLoading || managerState.isTesting}
              >
                <TestTube className="mr-2 size-4" />
                {managerState.isTesting ? 'Testing...' : 'Test Connection'}
              </Button>
            )}

            {/* Save Button */}
            <Button
              type="submit"
              onClick={form.handleSubmit(onSubmit)}
              disabled={managerState.isLoading || managerState.isTesting}
            >
              <Save className="mr-2 size-4" />
              {managerState.isLoading ? 'Saving...' : 'Save Integration'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}